import React, { createContext, useContext, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

type Language = 'en' | 'ur' | 'fa' | 'ar'

interface LanguageContextType {
  language: Language
  setLanguage: (lang: Language) => void
  isRTL: boolean
  direction: 'ltr' | 'rtl'
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined)

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const { i18n } = useTranslation()
  const [language, setLanguageState] = useState<Language>('en')

  // RTL languages
  const rtlLanguages: Language[] = ['ar', 'fa', 'ur']
  const isRTL = rtlLanguages.indexOf(language) !== -1
  const direction = isRTL ? 'rtl' : 'ltr'

  const setLanguage = (lang: Language) => {
    setLanguageState(lang)
    i18n.changeLanguage(lang)
    localStorage.setItem('language', lang)
    
    // Calculate new direction
    const newIsRTL = rtlLanguages.indexOf(lang) !== -1
    const newDirection = newIsRTL ? 'rtl' : 'ltr'
    
    // Update document direction and language
    document.documentElement.dir = newDirection
    document.documentElement.lang = lang
    
    // Force reflow to ensure changes take effect
    document.documentElement.offsetHeight
  }

  useEffect(() => {
    // Get saved language or detect from browser
    const savedLanguage = localStorage.getItem('language') as Language
    const browserLanguage = navigator.language.split('-')[0] as Language
    
    const supportedLanguages = ['en', 'ur', 'fa', 'ar']
    const initialLanguage = savedLanguage || 
      (supportedLanguages.indexOf(browserLanguage) !== -1 ? browserLanguage : 'en')
    
    setLanguage(initialLanguage)
  }, [])

  useEffect(() => {
    // Update document direction when language changes
    document.documentElement.dir = direction
    document.documentElement.lang = language
  }, [language, direction])

  return (
    <LanguageContext.Provider value={{ language, setLanguage, isRTL, direction }}>
      {children}
    </LanguageContext.Provider>
  )
}

export function useLanguage() {
  const context = useContext(LanguageContext)
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider')
  }
  return context
}
